// screens/Login/ConfirmSignUpScreen.js
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  Text,
  TextInput,
  View,
} from 'react-native';

import mystyles from '../../assets/styles/mystyles';
import Auth from '../../auth/AuthHelper';
import {SafeAreaView} from 'react-native-safe-area-context';

const RESEND_WAIT_SECONDS = 60;

export default function ConfirmSignUpWithPinScreen({ route, navigation }) {
  const username = route?.params?.username || '';

  const [code, setCode] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(RESEND_WAIT_SECONDS);

  useEffect(() => {
    // start countdown for resend availability
    if (secondsLeft <= 0) return;
    const id = setInterval(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearInterval(id);
  }, [secondsLeft]);

  const handleCodeChange = (text) => {
    // keep digits only, max 6
    const onlyDigits = text.replace(/\D/g, '').slice(0, 6);
    setCode(onlyDigits);
    setMessage('');
  };

  const handleConfirm = async () => {
    if (!username) {
      setMessage('Missing username. Please go back and try again.');
      return;
    }
    if (code.length !== 6) {
      setMessage('Enter the 6-digit code.');
      return;
    }

    setSubmitting(true);
    setMessage('');
    try {
      // Adjust the call shape if your Auth helper expects different arguments
      await Auth.confirmSignUp({ username, code });

      Alert.alert('Success', 'Your phone number has been verified.', [
        {
          text: 'OK',
          onPress: () => {
            // Navigate to your sign-in screen (update the route name if different)
            navigation.navigate('SignInScreen', { username });
          },
        },
      ]);
    } catch (e) {
      setMessage(e?.message || 'Verification failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!username) {
      setMessage('Missing username. Please go back and try again.');
      return;
    }
    setResending(true);
    setMessage('');
    try {
      await Auth.resendSignUp({ username });
      setMessage('A new code has been sent via SMS.');
      setSecondsLeft(RESEND_WAIT_SECONDS);
    } catch (e) {
      setMessage(e?.message || 'Unable to resend code. Please try again later.');
    } finally {
      setResending(false);
    }
  };

  return (
    <SafeAreaView style={mystyles.safe}>
      <KeyboardAvoidingView
        style={mystyles.flex}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <ScrollView contentContainerStyle={mystyles.scroll} keyboardShouldPersistTaps="handled">
          <View style={mystyles.card}>
            <View style={mystyles.form}>
              <Text style={mystyles.title}>Verify your phone</Text>
              <Text style={mystyles.subtitle}>
                Enter the 6-digit code we sent via SMS to your phone number.
              </Text>

              <TextInput
                style={mystyles.input}
                value={code}
                onChangeText={handleCodeChange}
                placeholder="6-digit code"
                keyboardType="number-pad"
                maxLength={6}
                autoFocus
                placeholderTextColor="#8A8A8E"
                textContentType="oneTimeCode"
              />

              <View style={mystyles.buttonWrapper}>
                <Button
                  title={submitting ? 'Verifying...' : 'Verify code'}
                  onPress={handleConfirm}
                  disabled={submitting || code.length !== 6}
                />
              </View>

              <View style={mystyles.inlineRow}>
                <Text style={{ marginRight: 8 }}>
                  Didn’t get the code?
                </Text>
                <Button
                  title={
                    secondsLeft > 0
                      ? `Resend in ${secondsLeft}s`
                      : resending
                        ? 'Resending...'
                        : 'Resend code'
                  }
                  onPress={handleResend}
                  disabled={resending || secondsLeft > 0}
                />
              </View>

              {message ? <Text style={mystyles.message}>{message}</Text> : null}

              <View style={mystyles.buttonWrapper}>
                <Button title="Change phone / Back" onPress={() => navigation.goBack()} />
              </View>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}
